import Button from '@atlaskit/button/new';
import type { HeadType } from '@atlaskit/dynamic-table/dist/types/types';
import { Flex, Text, xcss } from '@atlaskit/primitives';
import Tooltip from '@atlaskit/tooltip';
import { useState } from 'react';
import { Link } from 'react-resource-router';

import { SettingsList } from '../../components/settings-list';
import { useGithubOrganisationList } from '../hooks';
import { createKey, parseCodeReviewerSettingValue } from '../utils';

import type { CRFlag } from './code-reviewer-flags';
import { CodeReviewerFlags } from './code-reviewer-flags';
import { OrganisationToggle } from './toggle';

const styles = xcss({ height: '50px' });
const orgHead: HeadType = {
	cells: [
		{
			key: 'name-head-cell',
			content: 'Name',
			isSortable: true,
			width: 20,
		},
		{
			key: 'url-head-cell',
			content: 'URL',
			isSortable: true,
			width: 35,
		},
		{
			key: 'status-head-cell',
			content: 'Code Reviewer status',
			isSortable: false,
			width: 25,
		},
		{
			key: 'action-head-cell',
			content: 'Repositories',
			isSortable: false,
			width: 20,
		},
	],
};

type GithubOrganisationListProps = {
	setSelectedOrg: (org: GH.Organisation) => void;
};
export const GithubOrganisationList = ({ setSelectedOrg }: GithubOrganisationListProps) => {
	const { error, loading, orgList, setOrgList } = useGithubOrganisationList();
	const [page, setPage] = useState<number>(0);
	const [flags, setFlags] = useState<CRFlag[]>([]);

	const orgRows = orgList?.map((org) => {
		const { subscriptionId, owner, ownerUrl } = org;
		const enableButton = org.autoreviewSettings
			? parseCodeReviewerSettingValue(org.autoreviewSettings.value)
			: false;

		const handleUpdateOrgList = (newValue: boolean) => {
			const newOrgList = orgList.map((newOrg) => {
				if (newOrg.subscriptionId === org.subscriptionId) {
					return {
						...newOrg,
						autoreviewSettings: {
							value: {
								autoreview_activated: newValue,
							},
							lastUpdatedTime: new Date(),
							containerId: newOrg.ownerUrl,
						},
					};
				}
				return newOrg;
			});

			const flagToAdd: CRFlag = {
				id: flags.length + 1,
				title: newValue ? 'Enabled Code Reviewer' : 'Disabled Code Reviewer',
				description: 'This might take up to 15 minutes',
			};

			setFlags((current) => [...current, flagToAdd]);

			setOrgList(newOrgList);
		};

		return {
			key: `row-${subscriptionId}-${owner} `,
			cells: [
				{
					key: createKey(owner),
					content: (
						<Flex xcss={styles} alignItems="center">
							<Text>{owner}</Text>
						</Flex>
					),
				},
				{
					key: createKey(ownerUrl),
					content: <Link href={ownerUrl}>{ownerUrl}</Link>,
				},
				{
					key: `${subscriptionId}-status-cell`,
					content: (
						<OrganisationToggle
							org={org}
							onToggle={(newValue: boolean) => handleUpdateOrgList(newValue)}
						/>
					),
				},
				{
					key: `${subscriptionId}-action-cell`,
					content: (
						<Tooltip
							content="Enable this organisation to gain access to repository settings"
							canAppear={() => !enableButton}
						>
							{(toolTipProps) => (
								<Button
									{...toolTipProps}
									onClick={() => setSelectedOrg(org)}
									isDisabled={!enableButton}
								>
									Manage repositories
								</Button>
							)}
						</Tooltip>
					),
				},
			],
		};
	});

	return (
		<>
			<SettingsList
				headingText="Organizations"
				error={error}
				loading={loading}
				tableHead={orgHead}
				tableRows={orgRows ?? []}
				currentPage={page}
				setCurrentPage={setPage}
			/>
			<CodeReviewerFlags flags={flags} setFlags={setFlags} />
		</>
	);
};
